import { useSelector } from 'react-redux';

import { useMediaQuery, useTheme } from '@mui/material';

import { SCREEN_WIDTH } from 'constants/ui';

import {
  selectIsDesktop,
  selectIsMobile,
  selectIsTablet,
} from 'store/global/selectors/globalUi.selectors';

/**
 * Hook for determining the current breakpoint.
 *
 * Uses MUI useMediaQuery with customized theme.
 * If ThemeProvider is unavailable — automatically falls back to Redux selectors.
 *
 * @returns {{
 *   isMobile: boolean,
 *   isTablet: boolean,
 *   isDesktop: boolean,
 *   isWideDesktop: boolean,
 *   isLargeScreen: boolean
 * }}
 */
export const useBreakpoints = () => {
  const theme = useTheme();
  const hasCustomBreakpoints = theme?.breakpoints?.values?.md === SCREEN_WIDTH.M;

  const muiIsMobile = useMediaQuery(
    hasCustomBreakpoints ? theme.breakpoints.down('md') : `(max-width: ${SCREEN_WIDTH.M - 1}px)`,
    { noSsr: true },
  );
  const muiIsTablet = useMediaQuery(
    hasCustomBreakpoints
      ? theme.breakpoints.between('md', 'lg')
      : `(min-width: ${SCREEN_WIDTH.M}px) and (max-width: ${SCREEN_WIDTH.L - 1}px)`,
    { noSsr: true },
  );
  const muiIsDesktop = useMediaQuery(
    hasCustomBreakpoints ? theme.breakpoints.up('lg') : `(min-width: ${SCREEN_WIDTH.L}px)`,
    { noSsr: true },
  );
  const muiIsWideDesktop = useMediaQuery(
    hasCustomBreakpoints ? theme.breakpoints.up('lgWide') : `(min-width: ${SCREEN_WIDTH.L_WIDE}px)`,
    { noSsr: true },
  );
  const muiIsLargeScreen = useMediaQuery(
    hasCustomBreakpoints ? theme.breakpoints.up('xl') : `(min-width: ${SCREEN_WIDTH.XL}px)`,
    { noSsr: true },
  );

  const reduxIsMobile = useSelector(selectIsMobile);
  const reduxIsTablet = useSelector(selectIsTablet);
  const reduxIsDesktop = useSelector(selectIsDesktop);

  return {
    isMobile: muiIsMobile ?? reduxIsMobile,
    isTablet: muiIsTablet ?? reduxIsTablet,
    isDesktop: muiIsDesktop ?? reduxIsDesktop,
    isWideDesktop: muiIsWideDesktop ?? false,
    isLargeScreen: muiIsLargeScreen ?? false,
  };
};
